import {
  ExclamationCircleOutlined,
  GiftOutlined,
  MessageOutlined,
  TagsOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Button,
  Card,
  ConfigProvider,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Spin,
  Table,
  Tooltip,
} from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import Swal from 'sweetalert2'
import customerApi from '../../api/customer.api'
import promotionApi from '../../api/promotion.api'
import pushApi from '../../api/push.api'
import KakaoTemplateModal from '../../components/customer/KakaoTemplateModal'
import ContentHeader from '../../components/shared/ContentHeader'
import { userState } from '../../store/user'
import { CustomDateButtonRadioTheme } from '../../styles/CustomAntdTheme'
import styles from '../../styles/product.module.scss'
import { CustomerAPIResponseMapper } from '../../utils/CustomerListMapper'

const { Option } = Select

// UserSegment enum (AppPushSendPage와 동일)
const UserSegment = {
  All: '전체',
  AD: '광고 수신 동의',
  MALE: '남성',
  FEMALE: '여성',
  BIRTHDAY_TODAY: '오늘 생일',
  BIRTHDAY_THIS_MONTH: '이번 달 생일',
  AGE_10S: '10대',
  AGE_20S: '20대',
  AGE_30S: '30대',
  AGE_40S: '40대',
  AGE_50S: '50대',
  AGE_60S: '60대 이상',
  IS_LIBRE_CONNECTED: '현재 리브레 연동 중',
  IS_LIBRE_NOT_CONNECTED: '현재 리브레 연동 안 함',
  HAS_LIBRE_CONNECTED: '리브레 연동 이력 있음',
  SIGNUP_ROUTE_INSTA: '인스타로 가입',
  SIGNUP_ROUTE_YOUTUBE: '유튜브로 가입',
  SELECTED_USERS: '개별 선택',
}

const CustomerCouponAndPointManagePage = () => {
  const [user, setUser] = useRecoilState(userState)
  const [selectedType, setSelectedType] = useState('point')
  const [segment, setSegment] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()
  const navigate = useNavigate()

  // 포인트 관련 상태
  const [pointData, setPointData] = useState({
    pointAmount: '',
    validDays: '',
    pointContent: '',
  })

  // 쿠폰 관련 상태
  const [selectedCoupon, setSelectedCoupon] = useState(null)
  const [couponList, setCouponList] = useState([])
  const [couponSearchOptions, setCouponSearchOptions] = useState({
    couponId: '',
    couponTitle: '',
  })

  // 카카오 템플릿 관련 상태
  const [isKakaoTemplateModalOpen, setIsKakaoTemplateModalOpen] =
    useState(false)
  const [selectedKakaoTemplate, setSelectedKakaoTemplate] = useState(null)
  const [kakaoTemplateVariables, setKakaoTemplateVariables] = useState({})
  const [templateVariables, setTemplateVariables] = useState([])
  const [kakaoTemplateData, setKakaoTemplateData] = useState({
    content: [],
    pageable: { pageNumber: 0, pageSize: 10 },
    totalElements: 0,
  })

  // 스케줄 발송 관련 상태
  const [scheduleTime, setScheduleTime] = useState(null)
  const [isScheduled, setIsScheduled] = useState(false)

  // 회원 목록 관련 상태 (AppPushSendPage와 동일)
  const [customers, setCustomers] = useState([])
  const [totalCustomerCount, setTotalCustomerCount] = useState({
    all: 0, // 전체 회원 수
    currentPageCount: 0, // 현재 페이지 회원 수
  })
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedUserIds, setSelectedUserIds] = useState([])
  const [searchOptions, setSearchOptions] = useState({
    searchType: 'total',
    searchValue: null,
    dateType: 'total',
    startDate: null,
    endDate: null,
  })
  const [totalSize, setTotalSize] = useState(0)
  const [currentPageNumber, setCurrentPageNumber] = useState(1)
  const [offset, setOffset] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  // 검색 타입 옵션 (AppPushSendPage와 동일)
  const searchTypeItems = [
    { searchType: 'total', searchText: '전체검색' },
    { searchType: 'id', searchText: '아이디' },
    { searchType: 'phone', searchText: '연락처' },
  ]

  const dateTypeItems = [
    { label: '전체', value: 'total' },
    { label: '오늘', value: 'today' },
    { label: '1주일', value: '1week' },
    { label: '1개월', value: '1month' },
    { label: '3개월', value: '3month' },
  ]

  // 회원 목록 컬럼 (AppPushSendPage와 동일)
  const columns = [
    {
      title: '회원번호',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '생일',
      dataIndex: 'birthday',
      key: 'birthday',
      render: (value) => (value ? dayjs(value).format('YYYY.MM.DD') : '-'),
    },
    {
      title: '연락처',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '광고 수신 허용 여부',
      dataIndex: 'ad_alert',
      key: 'ad_alert',
      render: (value) => (value === true ? '허용' : '거절'),
    },
    {
      title: '가입일',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => (value ? dayjs(value).format('YYYY.MM.DD') : '-'),
    },
  ]

  // 쿠폰 목록 조회
  const getReadCouponList = async () => {
    setIsLoading(true)
    try {
      const param = {
        offset: 0,
        pageSize: 1000, // 모든 쿠폰을 가져오기 위해 큰 값 설정
        couponId: couponSearchOptions.couponId,
        couponTitle: couponSearchOptions.couponTitle,
        orderType: 'updatedAt',
        orderBy: 'desc',
      }
      const res = await promotionApi.readCouponList(param, user.id)
      if (res.data.code === 200) {
        setCouponList(res.data.result.couponDTOList)
      } else {
        Swal.fire({
          icon: 'error',
          title: '쿠폰 목록을 불러오는데 실패했습니다.',
          text: res.data.message,
        })
      }
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      console.log('couponList read error', error)
    }
  }

  // 쿠폰 타입이 변경될 때 쿠폰 목록 조회
  useEffect(() => {
    if (selectedType === 'coupon') {
      getReadCouponList()
    }
  }, [selectedType])

  // 회원 목록 조회 (AppPushSendPage와 동일)
  useEffect(() => {
    if (segment?.includes('SELECTED_USERS')) {
      getReadMemberList('initial')
    }
  }, [segment])

  useEffect(() => {
    if (segment?.includes('SELECTED_USERS')) {
      getReadMemberList()
    }
  }, [offset, pageSize])

  const getReadMemberList = async (type = '') => {
    setIsLoading(true)
    let productType = null
    let categoryId = null
    try {
      let param
      if (type === 'initial') {
        param = {
          offset: 0,
          pageSize: 10,
          searchType: 'total',
          searchValue: null,
          startDate: null,
          endDate: null,
          orderType: 'created_at',
          orderBy: 'desc',
          signupReferralCode: null,
          productType: productType,
          categoryId: categoryId,
        }
      } else if (type === 'search') {
        param = {
          offset: 0,
          pageSize: 10,
          searchType: searchOptions.searchType,
          searchValue: searchOptions.searchValue,
          startDate: searchOptions.startDate
            ? searchOptions.startDate.format('YYYY-MM-DD')
            : null,
          endDate: searchOptions.endDate
            ? searchOptions.endDate.format('YYYY-MM-DD')
            : null,
          orderType: 'created_at',
          orderBy: 'desc',
          signupReferralCode: searchOptions.signupReferralCode,
          productType: productType,
          categoryId: categoryId,
        }
      } else {
        param = {
          offset: offset,
          pageSize: pageSize,
          searchType: searchOptions.searchType,
          searchValue: searchOptions.searchValue,
          startDate: searchOptions.startDate
            ? searchOptions.startDate.format('YYYY-MM-DD')
            : null,
          endDate: searchOptions.endDate
            ? searchOptions.endDate.format('YYYY-MM-DD')
            : null,
          orderType: 'created_at',
          orderBy: 'desc',
          signupReferralCode: searchOptions.signupReferralCode,
          productType: productType,
          categoryId: categoryId,
        }
      }

      const res = await customerApi.readCustomerList(param, user.id)
      if (res.data.code === 200) {
        setCustomers(
          CustomerAPIResponseMapper.getUserList(res.data.result.userList),
        )
        setTotalSize(res.data.result.totalCnt)

        // initial일 때만 전체 회원 수 저장
        if (type === 'initial') {
          setTotalCustomerCount((prev) => ({
            ...prev,
            all: res.data.result.totalCnt,
          }))
        }

        // 현재 페이지 회원 수 업데이트
        setTotalCustomerCount((prev) => ({
          ...prev,
          currentPageCount: res.data.result.userList.length,
        }))
      } else {
        Swal.fire({
          icon: 'error',
          title: '회원 목록을 조회해오지 못하였습니다.',
          text: res.data.message,
        })
      }
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      setCustomers([])
      setTotalSize(0)
      setTotalCustomerCount((prev) => ({
        ...prev,
        currentPageCount: 0,
      }))
      console.log('getReadMemberList read error', error)
    }
  }

  const handleDateTypeChange = (e) => {
    const value = e.target.value
    switch (value) {
      case 'total':
        setSearchOptions({
          ...searchOptions,
          dateType: value,
          startDate: null,
          endDate: null,
        })
        break
      case 'today':
        setSearchOptions({
          ...searchOptions,
          dateType: value,
          startDate: dayjs().startOf('day'),
          endDate: dayjs().endOf('day'),
        })
        break
      case '1week':
        setSearchOptions({
          ...searchOptions,
          dateType: value,
          startDate: dayjs().subtract(7, 'days').startOf('day'),
          endDate: dayjs().endOf('day'),
        })
        break
      case '1month':
        setSearchOptions({
          ...searchOptions,
          dateType: value,
          startDate: dayjs().subtract(1, 'months').startOf('day'),
          endDate: dayjs().endOf('day'),
        })
        break
      case '3month':
        setSearchOptions({
          ...searchOptions,
          dateType: value,
          startDate: dayjs().subtract(3, 'months').startOf('day'),
          endDate: dayjs().endOf('day'),
        })
        break
    }
  }

  const handleSearch = () => {
    setOffset(0)
    setPageSize(10)
    setCurrentPageNumber(1)
    getReadMemberList('search')
  }

  const handleInitialBtnClick = () => {
    setSearchOptions({
      searchType: 'total',
      searchValue: null,
      dateType: 'total',
      startDate: null,
      endDate: null,
    })
    setOffset(0)
    setPageSize(10)
    setCurrentPageNumber(1)
    getReadMemberList('initial')
  }

  const handleChangePagination = (pageNumber, pageSize) => {
    const offset = (pageNumber - 1) * pageSize
    setCurrentPageNumber(pageNumber)
    setOffset(offset)
    setPageSize(pageSize)
  }

  // 회원 세그먼트 옵션 (AppPushSendPage와 동일)
  const segmentOptions = Object.entries(UserSegment).map(([key, value]) => ({
    label: value,
    value: key,
  }))

  // 세그먼트 변경 핸들러 (AppPushSendPage와 동일)
  const handleSegmentChange = (value) => {
    //리브레 현재 연동 중, 현재 연동 안 함 목록 체크
    let v = value
    if (
      v.includes('IS_LIBRE_CONNECTED') &&
      v.includes('IS_LIBRE_NOT_CONNECTED')
    ) {
      // 마지막 선택만 남기고 반대 조건 제거
      v = v.filter(
        (item) =>
          item === value[value.length - 1] ||
          (item !== 'IS_LIBRE_CONNECTED' && item !== 'IS_LIBRE_NOT_CONNECTED'),
      )
    }

    setSegment(v)
    setSelectedRowKeys([])
    setSelectedUserIds([])
    if (!value.includes('SELECTED_USERS')) {
      setCustomers([])
      setTotalSize(0)
      setTotalCustomerCount((prev) => ({
        ...prev,
        currentPageCount: 0,
      }))
    }
  }

  const isFormValid = () => {
    if (!segment || segment.length === 0) return false
    if (!selectedKakaoTemplate) return false
    if (selectedType === 'point') {
      if (!pointData.pointAmount || !pointData.validDays) return false
    } else {
      if (!selectedCoupon) return false
    }
    if (segment?.includes('SELECTED_USERS') && selectedUserIds.length === 0) {
      return false
    }

    // 예약 발송 시 시간 선택 필수
    if (isScheduled && !scheduleTime) {
      return false
    }

    // 템플릿 변수 필수 검증 추가
    if (selectedKakaoTemplate?.variableList) {
      const variables = Array.isArray(selectedKakaoTemplate.variableList)
        ? selectedKakaoTemplate.variableList
        : selectedKakaoTemplate.variableList.split(',').map((v) => v.trim())

      for (const variable of variables) {
        // 고객명, 이름 관련 변수는 자동으로 처리되므로 제외
        if (
          variable === '고객명' ||
          variable === '이름' ||
          variable === 'name' ||
          variable === 'customer'
        ) {
          continue
        }

        // 다른 변수들은 필수 입력
        const variableValue = kakaoTemplateVariables[variable]
        if (
          !variableValue ||
          (typeof variableValue === 'string' && variableValue.trim() === '')
        ) {
          return false
        }
      }
    }

    return true
  }

  // 지급/발급 + 알림톡 발송
  const handleSubmit = async () => {
    try {
      setIsLoading(true)

      // 필수 필드 검증
      if (!segment) {
        Swal.fire({
          icon: 'error',
          title: '발송 대상을 선택해주세요.',
          text: '발송 대상을 선택해주세요.',
        })
        setIsLoading(false)
        return
      }

      if (!selectedKakaoTemplate) {
        Swal.fire({
          icon: 'error',
          title: '카카오 템플릿을 선택해주세요.',
          text: '카카오 템플릿을 선택해주세요.',
        })
        setIsLoading(false)
        return
      }

      // 예약 발송 시 시간 검증
      if (isScheduled && !scheduleTime) {
        Swal.fire({
          icon: 'error',
          title: '예약 발송을 선택하셨습니다. 예약 시간을 선택해주세요.',
          text: '예약 발송을 선택하셨습니다. 예약 시간을 선택해주세요.',
        })
        setIsLoading(false)
        return
      }

      // 템플릿 변수 필수 검증
      if (selectedKakaoTemplate?.variableList) {
        const variables = Array.isArray(selectedKakaoTemplate.variableList)
          ? selectedKakaoTemplate.variableList
          : selectedKakaoTemplate.variableList.split(',').map((v) => v.trim())

        const missingVariables = []
        for (const variable of variables) {
          // 고객명, 이름 관련 변수는 자동으로 처리되므로 제외
          if (
            variable === '고객명' ||
            variable === '이름' ||
            variable === 'name' ||
            variable === 'customer'
          ) {
            continue
          }

          // 다른 변수들은 필수 입력
          const variableValue = kakaoTemplateVariables[variable]
          if (
            !variableValue ||
            (typeof variableValue === 'string' && variableValue.trim() === '')
          ) {
            missingVariables.push(variable)
          }
        }

        if (missingVariables.length > 0) {
          Swal.fire({
            icon: 'error',
            title: `다음 템플릿 변수들을 입력해주세요:`,
            text: missingVariables.join(', '),
          })
          setIsLoading(false)
          return
        }
      }

      const pointParam = {
        userSegment: segment,
        selectedUserIds: segment?.includes('SELECTED_USERS')
          ? selectedUserIds
          : null,
        pointAmount: pointData.pointAmount,
        validDays: pointData.validDays,
        adminComment: pointData.pointContent || null,
        templateId: selectedKakaoTemplate?.kakaoTemplateId.trim(),
        interpolations: Object.keys(kakaoTemplateVariables).reduce(
          (acc, key) => {
            if (key === '고객명' || key === '이름') {
              acc[`#{${key}}`] = '{{name}}'
            } else if (key === 'url') {
              acc[`#{${key}}`] = String(kakaoTemplateVariables[key]).trim()
            } else if (kakaoTemplateVariables[key]) {
              acc[`#{${key}}`] = kakaoTemplateVariables[key]
            }
            return acc
          },
          {},
        ),
        ...(isScheduled &&
          scheduleTime && {
            scheduleTime: scheduleTime.format('YYYY-MM-DD HH:mm:ss'),
          }),
      }

      const couponParam = {
        userSegment: segment,
        selectedUserIds: segment?.includes('SELECTED_USERS')
          ? selectedUserIds
          : null,
        couponId: selectedCoupon?.couponId,
        templateId: selectedKakaoTemplate?.kakaoTemplateId.trim(),
        interpolations: Object.keys(kakaoTemplateVariables).reduce(
          (acc, key) => {
            if (key === '고객명' || key === '이름') {
              acc[`#{${key}}`] = '{{name}}' // liquid 템플릿 변수
            } else if (key === 'url') {
              acc[`#{${key}}`] = String(kakaoTemplateVariables[key]).trim()
            } else if (kakaoTemplateVariables[key]) {
              acc[`#{${key}}`] = kakaoTemplateVariables[key]
            }
            return acc
          },
          {},
        ),
        ...(isScheduled &&
          scheduleTime && {
            scheduleTime: scheduleTime.format('YYYY-MM-DD HH:mm:ss'),
          }),
      }

      if (selectedType === 'point') {
        const res = await pushApi.pointWithKakaoAlert(pointParam)
        if (res.status === 200) {
          Swal.fire({
            icon: 'success',
            title: '포인트 지급 및 알림톡 발송 완료!',
            text: '포인트 지급 및 알림톡 발송이 완료되었습니다.',
          }).then(() => {
            navigate('/customer/kakao-history')
          })
        } else {
          Swal.fire({
            icon: 'error',
            title: '포인트 지급 및 알림톡 발송 실패!',
            text: '포인트 지급 및 알림톡 발송이 실패했습니다.',
          })
        }
      } else {
        const res = await pushApi.couponWithKakaoAlert(couponParam)
        if (res.status === 200) {
          Swal.fire({
            icon: 'success',
            title: '쿠폰 발급 및 알림톡 발송 완료!',
            text: '쿠폰 발급 및 알림톡 발송이 완료되었습니다.',
          }).then(() => {
            navigate('/customer/kakao-history')
          })
        } else {
          Swal.fire({
            icon: 'error',
            title: '쿠폰 발급 및 알림톡 발송 실패!',
            text: '쿠폰 발급 및 알림톡 발송이 실패했습니다.',
          })
        }
      }
    } catch (err) {
      console.error('handleSubmit error:', err)
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }

  const getKakaoTemplateList = async (page = 0) => {
    try {
      const res = await customerApi.readKakaoTemplateList({
        page: page,
        size: 10,
      })
      if (res.status === 200) {
        setKakaoTemplateData({
          content: res.data.content,
          pageable: { pageNumber: page, pageSize: 10 },
          totalElements: res.data.totalElements,
        })
      } else {
        console.log('API 응답 상태가 200이 아닙니다:', res.status)
      }
    } catch (error) {
      if (error.response) {
        // 500 에러인 경우 사용자에게 알림
        if (error.response.status === 500 && page === 2) {
          alert(
            '3페이지에서 서버 오류가 발생했습니다. 서버 개발팀에 문의가 필요합니다.',
          )
        }
      }
    }
  }

  useEffect(() => {
    getKakaoTemplateList()
  }, [])

  // 카카오 템플릿 페이지 변경 핸들러
  const handleKakaoTemplatePageChange = (page) => {
    getKakaoTemplateList(page - 1) // API는 0부터 시작하므로 -1
  }

  // 카카오 템플릿 선택 핸들러
  const handleKakaoTemplateSelect = (template) => {
    setSelectedKakaoTemplate(template)
    setIsKakaoTemplateModalOpen(false)
  }

  // 쿠폰 정보를 기반으로 템플릿 변수 자동 매핑
  const mapCouponToTemplateVariables = (coupon, variables) => {
    const mappedVariables = {}

    variables.forEach((variable) => {
      // 고객명, 이름 관련 변수들은 비활성화 상태로 유지
      const isDisabled =
        variable === '고객명' ||
        variable === '이름' ||
        variable === 'name' ||
        variable === 'customer'

      if (isDisabled) {
        mappedVariables[variable] = ''
        return
      }

      // 쿠폰 정보 기반 자동 매핑
      switch (variable) {
        case '쿠폰명':
        case '쿠폰이름':
        case '쿠폰제목':
          mappedVariables[variable] = coupon?.couponTitle || ''
          break
        case '쿠폰번호':
        case '쿠폰ID':
          mappedVariables[variable] = coupon?.couponId || ''
          break
        case '쿠폰설명':
        case '쿠폰내용':
          mappedVariables[variable] = coupon?.couponBody || ''
          break
        case '할인율':
        case '할인액':
          if (coupon?.couponScoreType === 'WON') {
            mappedVariables[variable] = `${coupon.couponScore}원`
          } else if (coupon?.couponScoreType === 'PERCENT') {
            mappedVariables[variable] = `${coupon.couponScore}%`
          } else {
            mappedVariables[variable] = ''
          }
          break
        case '할인금액':
        case '할인원':
          if (coupon?.couponScoreType === 'WON') {
            mappedVariables[variable] = `${coupon.couponScore}원`
          } else {
            mappedVariables[variable] = ''
          }
          break
        case '쿠폰시작일':
        case '쿠폰시작날짜':
          if (coupon?.couponFrom) {
            mappedVariables[variable] = dayjs(coupon.couponFrom).format(
              'YYYY년 MM월 DD일',
            )
          } else {
            mappedVariables[variable] = ''
          }
          break
        case '쿠폰만료일':
        case '쿠폰종료일':
        case '쿠폰만료날짜':
          if (coupon?.couponExpirationTo) {
            mappedVariables[variable] = dayjs(coupon.couponExpirationTo).format(
              'YYYY년 MM월 DD일',
            )
          } else {
            mappedVariables[variable] = ''
          }
          break
        case '쿠폰사용기간':
        case '쿠폰유효기간':
          if (coupon?.couponFrom && coupon?.couponTo) {
            const startDate = dayjs(coupon.couponFrom).format(
              'YYYY년 MM월 DD일',
            )
            const endDate = dayjs(coupon.couponTo).format('YYYY년 MM월 DD일')
            mappedVariables[variable] = `${startDate} ~ ${endDate}`
          } else {
            mappedVariables[variable] = ''
          }
          break
        case '쿠폰종류':
        case '쿠폰타입':
          switch (coupon?.couponType) {
            case 'CATEGORY':
              mappedVariables[variable] = '카테고리'
              break
            case 'BRAND':
              mappedVariables[variable] = '브랜드'
              break
            case 'PRODUCT':
              mappedVariables[variable] = '상품'
              break
            case 'ALL':
              mappedVariables[variable] = '전체 상품 대상 이벤트'
              break
            default:
              mappedVariables[variable] = ''
          }
          break
        case '포인트액':
        case '포인트수량':
        case '포인트':
          mappedVariables[variable] = pointData.pointAmount || ''
          break
        case '지급사유':
        case '포인트사유':
        case '지급내용':
          mappedVariables[variable] =
            pointData.pointContent || '이벤트 소멸 포인트'
          break
        default:
          mappedVariables[variable] = ''
      }
    })

    // console.log('최종 매핑 결과:', mappedVariables)
    return mappedVariables
  }

  // 쿠폰이 선택되거나 템플릿이 변경될 때 변수 자동 매핑
  useEffect(() => {
    if (selectedKakaoTemplate?.variableList) {
      let variables = []
      if (Array.isArray(selectedKakaoTemplate.variableList)) {
        variables = selectedKakaoTemplate.variableList
      } else if (typeof selectedKakaoTemplate.variableList === 'string') {
        variables = selectedKakaoTemplate.variableList
          .split(',')
          .map((v) => v.trim())
      }

      setTemplateVariables(variables)
    }
  }, [selectedKakaoTemplate])
  useEffect(() => {
    if (templateVariables.length > 0) {
      if (selectedType === 'coupon' && selectedCoupon) {
        // 쿠폰이 선택된 경우: 쿠폰 정보로 매핑
        const mappedVariables = mapCouponToTemplateVariables(
          selectedCoupon,
          templateVariables,
        )
        setKakaoTemplateVariables(mappedVariables)
      } else if (selectedType === 'point') {
        // 포인트인 경우: 포인트 정보로 매핑
        const mappedVariables = mapCouponToTemplateVariables(
          null, // 쿠폰은 null이지만 포인트 변수는 매핑됨
          templateVariables,
        )
        setKakaoTemplateVariables(mappedVariables)
      } else {
        // 초기화
        const initialVariables = {}
        templateVariables.forEach((variable) => {
          initialVariables[variable] = ''
        })
        setKakaoTemplateVariables(initialVariables)
      }
    }
  }, [selectedCoupon, templateVariables, selectedType, pointData])

  return (
    <Spin spinning={isLoading}>
      <div style={{ width: '100%', paddingBottom: '8rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            background: '#fff',
          }}
        >
          <ContentHeader
            title={selectedType === 'point' ? '포인트 지급 ' : '쿠폰 발급'}
          />
          <Tooltip title="선택한 회원들에게 포인트 또는 쿠폰을 지급하고, 알림톡을 발송합니다.">
            <ExclamationCircleOutlined
              style={{
                color: 'red',
                fontSize: '20px',
                cursor: 'help',
              }}
            />
          </Tooltip>
        </div>

        {/* 상단 드롭다운 + 헤더 */}
        <Card
          title={
            <div className={styles['product-register-form-title']}>
              <TagsOutlined />
              <span style={{ marginLeft: '10px' }}>지급 구분</span>
            </div>
          }
          style={{ marginBottom: 24 }}
        >
          <div
            style={{
              display: 'flex',
              gap: 16,
              alignItems: 'center',
            }}
          >
            <Select
              required
              value={selectedType}
              onChange={setSelectedType}
              style={{ width: 160 }}
              className={styles['product-search-select']}
            >
              <Option value="point">포인트</Option>
              <Option value="coupon">쿠폰</Option>
            </Select>
          </div>
        </Card>

        {/* 지급 및 발송 대상(세그먼트) 선택 */}
        <Card
          title={
            <div className={styles['product-register-form-title']}>
              <UserOutlined />
              <span style={{ marginLeft: '10px' }}>지급 및 발송 대상</span>
              {segment?.includes('SELECTED_USERS') && (
                <>
                  <span style={{ marginLeft: '10px', color: '#1890ff' }}>
                    {(selectedUserIds?.length || 0).toLocaleString()}명 선택됨
                  </span>
                  <span> / 총 {totalCustomerCount.all.toLocaleString()}명</span>
                  {searchOptions.searchType !== 'total' && (
                    <span style={{ marginLeft: '10px', color: '#666' }}>
                      (현재 페이지 {totalCustomerCount.currentPageCount}명)
                    </span>
                  )}
                  <Button
                    onClick={() => {
                      setSelectedRowKeys([])
                      setSelectedUserIds([])
                    }}
                    style={{ marginLeft: '10px' }}
                  >
                    회원 선택 초기화
                  </Button>
                </>
              )}
            </div>
          }
          style={{ marginBottom: 24 }}
        >
          <Form.Item
            label={
              <span>
                발송 대상
                <Tooltip
                  title="여러 조건을 선택하면 모든 조건을 동시에 만족하는 회원에게만 발송됩니다.
                예시: '광고 허락' + '60대 이상' → 두 조건을 모두 만족하는 회원만 발송"
                >
                  <ExclamationCircleOutlined
                    style={{ color: 'red', marginLeft: 6, cursor: 'help' }}
                  />
                </Tooltip>
              </span>
            }
            required
            style={{ marginBottom: 16 }}
            labelCol={{ flex: '0 0 100px' }}
            wrapperCol={{ flex: '1 1 auto' }}
            labelAlign="left"
          >
            <Select
              mode="multiple"
              style={{ width: '300px' }}
              placeholder="발송 대상을 선택하세요"
              options={segmentOptions}
              value={segment}
              onChange={handleSegmentChange}
            />
          </Form.Item>

          {segment?.includes('SELECTED_USERS') && (
            <>
              <div className={styles['product-search-box-wrap']}>
                {/* 검색 폼 */}
                <Form.Item
                  className={styles['center-label']}
                  label="검색"
                  labelAlign="left"
                  colon={false}
                  labelCol={{ span: 3 }}
                  wrapperCol={{ span: 21 }}
                >
                  <Select
                    value={searchOptions.searchType}
                    onChange={(value) =>
                      setSearchOptions({
                        ...searchOptions,
                        searchType: value,
                      })
                    }
                    style={{ width: 200, textAlign: 'left' }}
                  >
                    {searchTypeItems.map((item, idx) => (
                      <Select.Option
                        key={`search_type_${idx}`}
                        value={item.searchType}
                      >
                        {item.searchText}
                      </Select.Option>
                    ))}
                  </Select>
                  <Input
                    className={styles['inquiry-search-input-wrap']}
                    placeholder="검색어를 입력하세요."
                    name="searchValue"
                    value={searchOptions.searchValue}
                    onChange={(e) =>
                      setSearchOptions({
                        ...searchOptions,
                        [e.target.name]: e.target.value,
                      })
                    }
                    onPressEnter={handleSearch}
                  />
                </Form.Item>
                <Divider
                  className={styles['product-register-form-input-divider']}
                />
                <Form.Item
                  className={styles['center-label']}
                  label="가입일"
                  labelAlign="left"
                  colon={false}
                  labelCol={{ span: 3 }}
                  wrapperCol={{ span: 21 }}
                >
                  <ConfigProvider theme={CustomDateButtonRadioTheme}>
                    <Row align={'middle'} style={{ gap: '5px' }}>
                      <Radio.Group
                        className={styles['options-list']}
                        options={dateTypeItems}
                        onChange={handleDateTypeChange}
                        value={searchOptions.dateType}
                        optionType="button"
                      />
                      <DatePicker
                        className={styles['order-search-date-wrap']}
                        format="YYYY/MM/DD"
                        allowClear={false}
                        value={searchOptions.startDate}
                        disabled={searchOptions.dateType === 'total'}
                        onChange={(value) => {
                          setSearchOptions({
                            ...searchOptions,
                            dateType: null,
                            startDate: value,
                          })
                        }}
                      />
                      ~
                      <DatePicker
                        className={styles['order-search-date-wrap']}
                        format="YYYY/MM/DD"
                        allowClear={false}
                        value={searchOptions.endDate}
                        disabled={searchOptions.dateType === 'total'}
                        onChange={(value) => {
                          setSearchOptions({
                            ...searchOptions,
                            dateType: null,
                            endDate: value,
                          })
                        }}
                      />
                    </Row>
                  </ConfigProvider>
                </Form.Item>
                <Row
                  className={styles['review-search-button-wrap']}
                  justify="center"
                  style={{ display: 'flex', gap: '10px' }}
                >
                  <Button
                    className={`${styles['review-search-button']} ${styles.search}`}
                    onClick={handleSearch}
                  >
                    검색
                  </Button>
                  <Button
                    className={`${styles['review-search-button']} ${styles.initial}`}
                    onClick={handleInitialBtnClick}
                  >
                    초기화
                  </Button>
                </Row>
              </div>
              <Table
                rowSelection={{
                  selectedRowKeys,
                  onChange: (selectedRowKeys) => {
                    // 현재 페이지에서 선택된 행들의 ID를 Set으로 관리
                    const currentPageIds = new Set(
                      customers.map((customer) => customer.id),
                    )
                    // 이전 selectedUserIds 중에서 현재 페이지의 항목이 아닌 것들은 유지
                    const previousSelectedIds = (selectedUserIds || []).filter(
                      (id) => !currentPageIds.has(id),
                    )
                    // 현재 페이지의 새로운 선택을 반영
                    const newSelectedIds = [
                      ...previousSelectedIds,
                      ...selectedRowKeys,
                    ]
                    setSelectedRowKeys(selectedRowKeys)
                    setSelectedUserIds(newSelectedIds)
                  },
                }}
                columns={columns}
                dataSource={customers}
                rowKey="id"
                pagination={{
                  current: currentPageNumber,
                  pageSize: pageSize,
                  total: totalSize,
                  onChange: handleChangePagination,
                  showSizeChanger: true,
                }}
                size="middle"
                scroll={{ x: 'max-content' }}
                loading={isLoading}
                style={{ marginTop: 20 }}
              />
            </>
          )}
        </Card>

        {/* 포인트/쿠폰 지급 폼 */}
        <Card
          title={
            <div className={styles['product-register-form-title']}>
              <GiftOutlined />
              <span style={{ marginLeft: '10px' }}>지급 정보</span>
            </div>
          }
          style={{ marginBottom: 24 }}
        >
          <Form form={form} layout="vertical">
            {selectedType === 'point' ? (
              <>
                <Form.Item
                  label="포인트액"
                  name="pointAmount"
                  rules={[
                    { required: true, message: '포인트액을 입력하세요.' },
                  ]}
                  className={styles['center-label']}
                  labelAlign="left"
                  colon={false}
                  labelCol={{ span: 3 }}
                  wrapperCol={{ span: 21 }}
                >
                  <InputNumber
                    value={pointData.pointAmount}
                    onChange={(value) =>
                      setPointData((prev) => ({ ...prev, pointAmount: value }))
                    }
                    min={1}
                    style={{ width: 200 }}
                    placeholder="포인트액 입력"
                  />
                </Form.Item>
                <Form.Item
                  label="소멸 기간"
                  name="validDays"
                  rules={[
                    { required: true, message: '소멸 기간을 입력하세요.' },
                    {
                      validator: (_, value) => {
                        if (value && value <= 0) {
                          return Promise.reject(
                            new Error('소멸 기간은 1일 이상이어야 합니다.'),
                          )
                        }
                        return Promise.resolve()
                      },
                    },
                  ]}
                  className={styles['center-label']}
                  labelAlign="left"
                  colon={false}
                  labelCol={{ span: 3 }}
                  wrapperCol={{ span: 21 }}
                >
                  <InputNumber
                    value={pointData.validDays}
                    onChange={(value) =>
                      setPointData((prev) => ({ ...prev, validDays: value }))
                    }
                    min={1}
                    style={{ width: 200 }}
                    placeholder="소멸 기간 입력 (일)"
                  />
                </Form.Item>
                <Form.Item
                  label="지급 사유"
                  name="pointContent"
                  className={styles['center-label']}
                  labelAlign="left"
                  colon={false}
                  labelCol={{ span: 3 }}
                  wrapperCol={{ span: 21 }}
                >
                  <Input
                    value={pointData.pointContent}
                    onChange={(e) =>
                      setPointData((prev) => ({
                        ...prev,
                        pointContent: e.target.value,
                      }))
                    }
                    style={{ width: 400 }}
                    placeholder="지급 사유 입력 (입력하지 않으면 '이벤트 소멸 포인트'로 설정됩니다)"
                  />
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item
                  label="쿠폰 선택"
                  name="couponId"
                  rules={[{ required: true, message: '쿠폰을 선택하세요.' }]}
                  className={styles['center-label']}
                  labelAlign="left"
                  colon={false}
                  labelCol={{ span: 3 }}
                  wrapperCol={{ span: 21 }}
                >
                  <Select
                    value={selectedCoupon?.id || ''}
                    onChange={(value) => {
                      const selected = couponList.find(
                        (coupon) => coupon.id === value,
                      )
                      setSelectedCoupon(selected)
                    }}
                    placeholder="쿠폰을 선택하세요"
                    style={{ width: 400 }}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {couponList.map((coupon) => (
                      <Option
                        key={coupon.id}
                        value={coupon.id}
                        label={coupon.couponTitle}
                      >
                        {coupon.couponTitle} ({coupon.couponId})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* 선택된 쿠폰 정보 표시 */}
                {selectedCoupon && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                      선택된 쿠폰 정보
                    </div>
                    <div
                      style={{
                        padding: 12,
                        backgroundColor: '#f0f8ff',
                        borderRadius: 6,
                        border: '1px solid #d9d9d9',
                      }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <strong>쿠폰명:</strong> {selectedCoupon.couponTitle}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <strong>쿠폰번호:</strong> {selectedCoupon.couponId}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <strong>쿠폰 종류:</strong>{' '}
                        {selectedCoupon.couponType === 'CATEGORY' && '카테고리'}
                        {selectedCoupon.couponType === 'BRAND' && '브랜드'}
                        {selectedCoupon.couponType === 'PRODUCT' && '상품'}
                        {selectedCoupon.couponType === 'ALL' &&
                          '전체 상품 대상 이벤트'}
                        {!['CATEGORY', 'BRAND', 'PRODUCT', 'ALL'].includes(
                          selectedCoupon.couponType,
                        ) && '-'}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <strong>할인:</strong>{' '}
                        {selectedCoupon.couponScoreType === 'WON'
                          ? `${selectedCoupon.couponScore}원`
                          : `${selectedCoupon.couponScore}%`}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <strong>사용기간:</strong>{' '}
                        {selectedCoupon.couponFrom && selectedCoupon.couponTo
                          ? `${dayjs(selectedCoupon.couponFrom).format('YYYY-MM-DD HH:mm')} ~ ${dayjs(selectedCoupon.couponTo).format('YYYY-MM-DD HH:mm')}`
                          : selectedCoupon.couponFrom
                            ? `${dayjs(selectedCoupon.couponFrom).format('YYYY-MM-DD HH:mm')} ~ `
                            : '-'}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <strong>다운로드 가능 여부:</strong>{' '}
                        {selectedCoupon.couponPublishAvailable
                          ? '다운로드 가능'
                          : '다운로드 불가능'}
                      </div>
                      <div>
                        <strong>사용 가능 여부:</strong>{' '}
                        {selectedCoupon.couponIsAvailable
                          ? '사용 가능'
                          : '사용 불가능'}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </Form>
        </Card>

        {/* 카카오 알림톡 발송 */}
        <Card
          title={
            <div className={styles['product-register-form-title']}>
              <MessageOutlined />
              <span style={{ marginLeft: '10px' }}>카카오 알림톡 발송</span>
            </div>
          }
          style={{ marginBottom: 24 }}
        >
          {/* 스케줄 발송 설정 */}
          <Form.Item
            label="발송 방식"
            className={styles['center-label']}
            labelAlign="left"
            colon={false}
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 21 }}
          >
            <Radio.Group
              value={isScheduled ? 'scheduled' : 'immediate'}
              onChange={(e) => {
                setIsScheduled(e.target.value === 'scheduled')
                if (e.target.value === 'immediate') {
                  setScheduleTime(null)
                }
              }}
            >
              <Radio value="immediate">즉시 발송</Radio>
              <Radio value="scheduled">예약 발송</Radio>
            </Radio.Group>
          </Form.Item>

          {isScheduled && (
            <Form.Item
              label="예약 시간"
              required
              className={styles['center-label']}
              labelAlign="left"
              colon={false}
              labelCol={{ span: 3 }}
              wrapperCol={{ span: 21 }}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                value={scheduleTime}
                onChange={setScheduleTime}
                placeholder="예약 발송 시간을 선택하세요"
                disabledDate={(current) => {
                  // 과거 날짜는 선택 불가
                  return current && current < dayjs().startOf('day')
                }}
                disabledTime={(date) => {
                  if (date && date.isSame(dayjs(), 'day')) {
                    // 오늘 날짜인 경우 현재 시간 이전은 선택 불가
                    const now = dayjs()
                    return {
                      disabledHours: () =>
                        Array.from({ length: now.hour() }, (_, i) => i),
                      disabledMinutes: (selectedHour) => {
                        if (selectedHour === now.hour()) {
                          return Array.from(
                            { length: now.minute() },
                            (_, i) => i,
                          )
                        }
                        return []
                      },
                    }
                  }
                  return {}
                }}
                style={{ width: 300 }}
              />
            </Form.Item>
          )}
          <Form.Item
            label="알림톡 템플릿"
            className={styles['center-label']}
            labelAlign="left"
            colon={false}
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 21 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Input
                value={selectedKakaoTemplate?.title || ''}
                placeholder="템플릿을 선택하세요"
                style={{ width: 300 }}
                readOnly
              />
              <Button
                type="primary"
                onClick={() => setIsKakaoTemplateModalOpen(true)}
              >
                템플릿 선택
              </Button>
            </div>
          </Form.Item>

          {/* 템플릿 변수 입력 */}
          {selectedKakaoTemplate && selectedKakaoTemplate.variableList && (
            <div style={{ marginTop: 16 }}>
              {/* 선택된 템플릿 내용 표시 */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                  선택된 템플릿 내용
                </div>
                <div
                  style={{
                    padding: 12,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 6,
                    border: '1px solid #d9d9d9',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.5,
                  }}
                >
                  {selectedKakaoTemplate.content || '내용이 없습니다.'}
                </div>
              </div>

              <div style={{ marginBottom: 16, fontWeight: 'bold' }}>
                템플릿 변수 입력 <span style={{ color: 'red' }}>*</span>
              </div>
              {(() => {
                const variables = Array.isArray(
                  selectedKakaoTemplate.variableList,
                )
                  ? selectedKakaoTemplate.variableList
                  : selectedKakaoTemplate.variableList
                      .split(',')
                      .map((v) => v.trim())

                return variables.map((variable, index) => {
                  // 고객명, 이름 관련 변수들은 비활성화
                  const isDisabled =
                    variable === '고객명' ||
                    variable === '이름' ||
                    variable === 'name' ||
                    variable === 'customer'

                  return (
                    <Form.Item
                      key={index}
                      label={
                        isDisabled ? (
                          variable
                        ) : (
                          <span>
                            {variable} <span style={{ color: 'red' }}>*</span>
                          </span>
                        )
                      }
                      style={{ marginBottom: 12 }}
                      labelCol={{ span: 3, style: { textAlign: 'left' } }}
                      wrapperCol={{ span: 21 }}
                      rules={
                        isDisabled
                          ? []
                          : [
                              {
                                required: true,
                                message: `${variable}을(를) 입력해주세요.`,
                              },
                            ]
                      }
                    >
                      <Input
                        value={kakaoTemplateVariables[variable] || ''}
                        onChange={(e) => {
                          setKakaoTemplateVariables((prev) => ({
                            ...prev,
                            [variable]: e.target.value,
                          }))
                        }}
                        placeholder={
                          isDisabled
                            ? '자동으로 입력됩니다'
                            : `${variable}을(를) 입력하세요 (필수)`
                        }
                        disabled={isDisabled}
                        style={{
                          backgroundColor: isDisabled ? '#f5f5f5' : 'white',
                          color: isDisabled ? '#999' : 'black',
                        }}
                      />
                    </Form.Item>
                  )
                })
              })()}
            </div>
          )}
        </Card>

        {/* 지급/발급 + 알림톡 발송 버튼 */}
        <div
          className={styles['product-register-fixed-footer']}
          style={{ textAlign: 'center' }}
        >
          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            disabled={!isFormValid()}
            className={`${styles['product-register-button']} ${styles.save}`}
            style={{ width: 'fit-content' }}
          >
            {selectedType === 'point'
              ? `포인트 지급 및 알림톡 ${isScheduled ? '예약' : '즉시'} 발송`
              : `쿠폰 발급 및 알림톡 ${isScheduled ? '예약' : '즉시'} 발송`}
          </Button>
        </div>
      </div>

      {isKakaoTemplateModalOpen && (
        <KakaoTemplateModal
          open={isKakaoTemplateModalOpen}
          onCancel={() => setIsKakaoTemplateModalOpen(false)}
          onSelect={handleKakaoTemplateSelect}
          kakaoTemplateList={kakaoTemplateData.content}
          totalElements={kakaoTemplateData.totalElements}
          currentPage={kakaoTemplateData.pageable.pageNumber + 1}
          onPageChange={handleKakaoTemplatePageChange}
          selectedKakaoTemplate={selectedKakaoTemplate}
        />
      )}
    </Spin>
  )
}

export default CustomerCouponAndPointManagePage
